import type { UserProfilePreferencesForm } from "@shared/schemas";
import type React from "react";

import { Button } from "@bcc/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { Upload } from "lucide-react";
import { useState } from "react";
import { Controller, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  setPreferencesViewChooseFileButtonTestId,
  setPreferencesViewRoasterInfoCardTestId,
  setPreferencesViewRoasterInfoDescriptionTestId,
  setPreferencesViewRoasterInfoTitleTestId,
  setPreferencesViewSaveLogoButtonTestId,
} from "../views/preferences-view.utils";

interface RoasterInfoSectionProps {
  roasterNameValue: string;
  logoPreview: string | null;
  uploadError: string | null;
  control: Control<UserProfilePreferencesForm>;
  isEditable: boolean;
  isUploading: boolean;
  hasPendingFile: boolean;
  onLogoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveLogo: () => void;
}

export const RoasterInfoSection = ({
  roasterNameValue,
  logoPreview,
  uploadError,
  control,
  isEditable,
  isUploading,
  hasPendingFile,
  onLogoUpload,
  onSaveLogo,
}: RoasterInfoSectionProps) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const [prevLogoPreview, setPrevLogoPreview] = useState(logoPreview);

  if (logoPreview !== prevLogoPreview) {
    setPrevLogoPreview(logoPreview);
    setImageError(false);
  }

  return (
    <Card data-testid={setPreferencesViewRoasterInfoCardTestId()}>
      <CardHeader>
        <CardTitle data-testid={setPreferencesViewRoasterInfoTitleTestId()}>{t("views.preferences.roasterInfo.title")}</CardTitle>
        <CardDescription data-testid={setPreferencesViewRoasterInfoDescriptionTestId()}>
          {t("views.preferences.roasterInfo.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4">
          <Label>{t("views.preferences.roasterInfo.logoLabel")}</Label>
          <div className="flex items-start gap-4">
            <div className="flex h-24 w-24 items-center justify-center rounded-lg border-2 border-dashed border-border-subtle bg-muted">
              {logoPreview && !imageError ? (
                <img
                  src={logoPreview}
                  alt={t("views.preferences.roasterInfo.logoPreviewAlt")}
                  className="h-full w-full object-contain p-2"
                  onError={() => setImageError(true)}
                />
              ) : (
                <Upload className="h-8 w-8 text-muted-foreground" />
              )}
            </div>

            <div className="flex-1 space-y-2">
              <Label htmlFor="logo-upload" className={!isEditable || isUploading ? "pointer-events-none" : "cursor-pointer"}>
                <div
                  data-testid={setPreferencesViewChooseFileButtonTestId()}
                  className={`inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ${
                    !isEditable || isUploading ? "opacity-50" : "hover:bg-primary/90"
                  }`}
                >
                  {t("views.preferences.roasterInfo.chooseFile")}
                </div>
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/tiff,image/webp"
                  className="hidden"
                  disabled={!isEditable || isUploading}
                  onChange={onLogoUpload}
                />
              </Label>
              <p className="text-xs text-muted-foreground">{t("views.preferences.roasterInfo.logoHint")}</p>
              {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
            </div>
          </div>

          {hasPendingFile && (
            <div className="flex gap-2">
              <Button
                type="button"
                data-testid={setPreferencesViewSaveLogoButtonTestId()}
                onClick={onSaveLogo}
                disabled={!isEditable || isUploading}
              >
                {isUploading ? t("views.preferences.roasterInfo.savingLogo") : t("views.preferences.roasterInfo.saveLogo")}
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="roaster-name">{t("views.preferences.roasterInfo.roasterName")}</Label>
          <Input id="roaster-name" value={roasterNameValue} disabled readOnly />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="phone">{t("views.preferences.roasterInfo.roasterPhone")}</Label>
          <Controller name="phone" control={control} render={({ field }) => <Input {...field} id="phone" type="tel" disabled readOnly />} />
        </div>
      </CardContent>
    </Card>
  );
};
